import { Result, Schema } from "effect";
import * as Runtime from "./runtime.ts";
import * as Source from "./source.ts";
import * as Development from "./development.ts";
import * as Review from "./review.ts";
import * as Mesh from "./mesh.ts";
import { HttpInternalError, HttpNotFoundError, HttpValidationError, FirstChangeMigrationFailure } from "./http.ts";
import { ContractDecodeError } from "./validation.ts";
import { InvalidDevelopmentIdentifier } from "../development-identifiers.ts";
import { StationPathLimitsError, StationPathValidationError } from "../station-path.ts";
import { AgenticTraceContextError } from "../agentic-correlation.ts";
import { HostedTestResultContractError } from "../hosted-test-result.ts";
import { WorktreeResponseError } from "../worktree-response.ts";
import { ServiceUnavailable } from "../health.ts";
import { RuntimeDependencyError } from "./dependency.ts";
import { DigestInvocationError } from "./digest.ts";
import { HistoryValidationError, HistoryNotFoundError, HistoryConflictError, HistoryMethodNotAllowedError } from "./history.ts";
import { BrowserValidationError, BrowserNotFoundError, BrowserMethodNotAllowedError, BrowserStorageError, TraceValidationError, TraceNotFoundError, TraceMethodNotAllowedError, TraceStorageError, TraceProbeUnavailableError } from "./observability.ts";
import { RecordingRequestError, RecordingStorageError } from "./recording.ts";
import { ResourceRequestError, ResourceStorageError } from "./resource.ts";

// These failures acquired status as HTTP projection metadata. Recovery still
// uses an explicit upstream statusCode, or the original code and identity.
// Native membership prevents a provider-owned object with a copied tag from
// suppressing its status. Failures that already had status retain its meaning.
// Defer constructor references until use because the shared barrel exports runners.
const isHttpProjectionFailure = /* @__PURE__ */ (() => Schema.is(Schema.suspend(() => Schema.Union([
  Schema.instanceOf(Runtime.BuildSingleFlightCapacityError),
  Schema.instanceOf(Runtime.CompilerWebSocketCapabilityError),
  Schema.instanceOf(Runtime.CompilerWebSocketFailure),
  Schema.instanceOf(Runtime.ContainerSessionError),
  Schema.instanceOf(Runtime.DuplicateBuildWaiterError),
  Schema.instanceOf(Runtime.PrepareCacheMissError),
  Schema.instanceOf(Runtime.BuildArtifactIntegrityError),
  Schema.instanceOf(Runtime.StoredSourceVersionStorageError),
  Schema.instanceOf(Runtime.ResponseBodyLimitError),
  Schema.instanceOf(Runtime.OperationDeadlineError),
  Schema.instanceOf(Runtime.PreparedDispatchReconstructionError),
  Schema.instanceOf(Runtime.WorkspaceBuildArtifactIntegrityError),
  Schema.instanceOf(Runtime.BrowserRestUnavailable),
  Schema.instanceOf(Runtime.BuildArtifactTooLargeError),
  Schema.instanceOf(Runtime.CompilerConfigurationError),
  Schema.instanceOf(Source.BlobTooLargeError),
  Schema.instanceOf(Source.BlobDigestMismatchError),
  Schema.instanceOf(Source.BlobCorruptionError),
  Schema.instanceOf(Source.TreeCorruptionError),
  Schema.instanceOf(Source.InconsistentCaptureError),
  Schema.instanceOf(Source.WorktreeQuotaError),
  Schema.instanceOf(Source.StationSourceIntegrityError),
  Schema.instanceOf(Source.HostedTestArtifactError),
  Schema.instanceOf(Development.ConflictResolutionError),
  Schema.instanceOf(Development.CandidateTargetRefreshError),
  Schema.instanceOf(Development.GateAuthorityError),
  Schema.instanceOf(Development.PublicationAuthorizationError),
  Schema.instanceOf(Development.PublicationTransactionError),
  Schema.instanceOf(Development.GateReportIntegrityError),
  Schema.instanceOf(Development.PublicationRevocationPendingError),
  Schema.instanceOf(Development.PublicationGateEvaluationRequiredError),
  Schema.instanceOf(Development.PublicationGateMutationAbortedError),
  Schema.instanceOf(Development.CandidateTargetMovedError),
  Schema.instanceOf(Development.CandidateCompositionConflictError),
  Schema.instanceOf(Review.SpecialistReviewStateError),
  Schema.instanceOf(Review.FindingDispositionError),
  Schema.instanceOf(Review.SpecialistProviderError),
  Schema.instanceOf(Review.SpecialistOutputValidationError),
  Schema.instanceOf(Review.SpecialistAttemptTimeout),
  Schema.instanceOf(Review.SpecialistAttemptCancellation),
  Schema.instanceOf(Mesh.MeshDockerCommandError),
  Schema.instanceOf(Mesh.MeshQuotaError),
  Schema.instanceOf(Mesh.MeshWorkloadConnectionError),
  Schema.instanceOf(Mesh.MeshWorkloadPoolCapacityError),
  Schema.instanceOf(HttpInternalError),
  Schema.instanceOf(HttpNotFoundError),
  Schema.instanceOf(HttpValidationError),
  Schema.instanceOf(FirstChangeMigrationFailure),
  Schema.instanceOf(ContractDecodeError),
  Schema.instanceOf(InvalidDevelopmentIdentifier),
  Schema.instanceOf(StationPathLimitsError),
  Schema.instanceOf(StationPathValidationError),
  Schema.instanceOf(AgenticTraceContextError),
  Schema.instanceOf(HostedTestResultContractError),
  Schema.instanceOf(WorktreeResponseError),
  Schema.instanceOf(ServiceUnavailable),
  Schema.instanceOf(RuntimeDependencyError),
  Schema.instanceOf(DigestInvocationError),
  Schema.instanceOf(HistoryValidationError),
  Schema.instanceOf(HistoryNotFoundError),
  Schema.instanceOf(HistoryConflictError),
  Schema.instanceOf(HistoryMethodNotAllowedError),
  Schema.instanceOf(BrowserValidationError),
  Schema.instanceOf(BrowserNotFoundError),
  Schema.instanceOf(BrowserMethodNotAllowedError),
  Schema.instanceOf(BrowserStorageError),
  Schema.instanceOf(RecordingRequestError),
  Schema.instanceOf(RecordingStorageError),
  Schema.instanceOf(ResourceRequestError),
  Schema.instanceOf(ResourceStorageError),
  Schema.instanceOf(TraceValidationError),
  Schema.instanceOf(TraceNotFoundError),
  Schema.instanceOf(TraceMethodNotAllowedError),
  Schema.instanceOf(TraceStorageError),
  Schema.instanceOf(TraceProbeUnavailableError)
]))))();

export const hasHttpProjectionStatus = (value: unknown): boolean =>
  Result.match(Result.try(() => isHttpProjectionFailure(value)), {
    onFailure: () => false,
    onSuccess: result => result
  });
